import { useState } from 'react';
import { PageHeader, TextSizeControl, ThemeToggle } from '../../layouts/Shells';
import { XMAS_DECOR_KEY, XMAS_SNOW_KEY } from '../../lib/christmas';
import { setChristmasPref, useChristmasPref, useInSeason } from '../christmas/useChristmas';
import { Button, InlineError, Input, Select, Skeleton } from '../../components/ui';
import { Row, RowGroup } from '../../components/ui/RowGroup';
import './settings.css';
import { DesktopAlertsButton, MessageSoundSwitch } from '../messages/DesktopAlertsControls';
import { useAuth } from '../auth/AuthProvider';
import type { Profile } from '../auth/access';
import {
  PREFERENCE_EVENTS,
  isKnownTimeZone,
  preferenceFor,
  preferenceLabel,
  slackSummary,
  timeZoneOptions,
  workspaceProblem,
} from './settingsLens';
import type { NotificationPreference, WorkspaceSettings } from './settingsLens';
import {
  useNotificationPreferences,
  useSaveNotificationPreference,
  useSaveProfileSettings,
  useSaveWorkspaceSettings,
  useWorkspaceSettings,
} from './hooks';

/**
 * Settings.
 *
 * Four sections on the 25/75 grid, in the order somebody looks for them:
 * themselves, then what reaches them, then the workspace, then billing.
 *
 * ── The sentence this page has to get right ────────────────────────────────
 *
 * A notification preference does NOT stop the Inbox row being written. It
 * stops the Slack fan-out. That distinction is invisible from a switch, so it
 * is said in words next to the switches — somebody who believes turning
 * "mentioned" off means they will never be told is going to miss something
 * that matters.
 *
 * ── Why the sections are separate components ───────────────────────────────
 *
 * Each one seeds form state from data that arrives later than the first
 * render. Seeding useState in the page body would capture the empty profile
 * and the null settings row and never correct itself: the name field would sit
 * blank next to somebody's filled-in name. Mounting the child only once its
 * data exists makes the seed correct by construction rather than by an effect
 * that re-syncs.
 */
export function SettingsPage() {
  const { access } = useAuth();
  const profile = access.status === 'approved' ? access.profile : null;
  const isAdmin = profile?.internal_role === 'tap_admin' || profile?.is_owner === true;

  const preferences = useNotificationPreferences();
  const workspace = useWorkspaceSettings();

  return (
    <>
      <PageHeader
        title="Settings"
        description="Your profile, what reaches you, and the workspace."
      />

      <div className="settings-groups">
        {profile === null ? (
          <Skeleton lines={2} label="Loading your profile" />
        ) : (
          <ProfileFields profile={profile} />
        )}

        {preferences.isPending ? (
          <Skeleton lines={4} label="Loading your notification preferences" />
        ) : (
          <NotificationFields rows={preferences.data ?? []} />
        )}

        {workspace.isPending ? (
          <Skeleton lines={3} label="Loading workspace settings" />
        ) : workspace.data == null ? (
          <p className="text-body-meta">Workspace settings could not be loaded.</p>
        ) : (
          <WorkspaceFields settings={workspace.data} isAdmin={isAdmin} />
        )}

        <DisplayFields />
        <MessageAlertFields />
        <BillingRows />
      </div>
    </>
  );
}

/**
 * Theme and text size, which have never had a home.
 *
 * Both live in the top bar and nowhere else, which makes them easy to miss and
 * — worse — leaves the surprising part unsaid: they are stored per DEVICE, so
 * the same person can be on Clay at their desk and Dark on their laptop and
 * reasonably think one of them failed to save. A settings page can say that;
 * two small controls in a top bar cannot.
 *
 * The controls themselves are the shell's, imported rather than rebuilt. Two
 * implementations of the theme switch would be two things to keep in step.
 */
function DisplayFields() {
  const inSeason = useInSeason();
  const decor = useChristmasPref(XMAS_DECOR_KEY);
  const snow = useChristmasPref(XMAS_SNOW_KEY);
  return (
    <RowGroup
      title="Display"
      note="These are per device, so your laptop and your phone can differ."
    >
      <Row icon="theme" label="Theme" control={<ThemeToggle />} />
      <Row icon="textSize" label="Text size" control={<TextSizeControl />} />
      <Row
        label="Christmas decorations"
        note={
          inSeason
            ? 'On by themselves until Jan 6, on whatever theme you use.'
            : 'Come back by themselves on Oct 1, on whatever theme you use.'
        }
        control={
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={decor}
              aria-label="Christmas decorations"
              onChange={(event) => {
                setChristmasPref(XMAS_DECOR_KEY, event.target.checked);
              }}
            />
          </label>
        }
      />
      <Row
        label="Falling snow"
        control={
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={snow}
              aria-label="Falling snow"
              onChange={(event) => {
                setChristmasPref(XMAS_SNOW_KEY, event.target.checked);
              }}
            />
          </label>
        }
      />
    </RowGroup>
  );
}

/**
 * Desktop alerts for Messages. Both per device, like Display: the browser
 * holds the pop-up permission, and the sound choice sits in this browser's
 * storage (tap.messages.sound). Nothing is saved to the account.
 */
function MessageAlertFields() {
  return (
    <RowGroup
      title="Message alerts"
      note="Per device. A pop-up and the sound come only when the Hub tab is in the background."
    >
      <Row icon="bell" label="Desktop pop-ups" control={<DesktopAlertsButton />} />
      <Row icon="bell" label="Sound for new messages" control={<MessageSoundSwitch />} />
    </RowGroup>
  );
}

/**
 * Read-only on purpose (Batch 10 scope): there is no billing schema and no
 * payment gateway. Nothing here is read from anywhere, so nothing here is a
 * figure or a date somebody could act on.
 */
function BillingRows() {
  return (
    <RowGroup
      title="Billing"
      note="Billing is not managed in the Hub. To change plan or seats, contact your TAP representative."
    >
      <Row icon="billing" label="Plan" value="Internal" />
      <Row label="Seats" value="Unlimited for 4ocean staff" />
    </RowGroup>
  );
}

// ── Profile ────────────────────────────────────────────────────────────────

function ProfileFields({ profile }: { profile: Profile }) {
  const saveProfile = useSaveProfileSettings();
  const [displayName, setDisplayName] = useState(profile.display_name);
  const [timeZone, setTimeZone] = useState(profile.time_zone);
  const [problem, setProblem] = useState('');

  function save() {
    if (displayName.trim() === '') {
      // Empty is legal in the column but not here: the shell falls back to the
      // email address when the name is blank, so saving an empty name looks
      // like the save failed.
      setProblem('A display name is needed — it is what everyone else sees.');
      return;
    }
    if (!isKnownTimeZone(timeZone)) {
      // The column is plain text, so nothing in the database stops a typo —
      // and a bad zone makes every date on this person's screen throw at
      // format time rather than here.
      setProblem('That is not a time zone this browser recognises.');
      return;
    }
    setProblem('');
    saveProfile.mutate(
      { displayName, timeZone },
      { onError: (cause: Error) => setProblem(cause.message) },
    );
  }

  return (
    <RowGroup
      title="Profile"
      note="Your picture comes from your Google account. Change it there and it follows here."
    >
      {/* The error sits INSIDE the card, above the field it is about. Outside
          it, a message about a name reads as a message about the page. */}
      {problem === '' ? null : (
        <div className="ui-row ui-row--plain settings-row-error">
          <InlineError>{problem}</InlineError>
        </div>
      )}
      <Row
        icon="profile"
        label="Display name"
        control={
          <Input
            label="Display name"
            labelHidden
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        }
      />
      <Row
        icon="clock"
        label="Time zone"
        control={
          <Select
            label="Time zone"
            labelHidden
            value={timeZone}
            onChange={(event) => setTimeZone(event.target.value)}
            options={timeZoneOptions().map((zone) => ({ value: zone, label: zone }))}
          />
        }
      />
      {/* Save is a row of its own rather than a button under the card: in a
          grouped list the actions live in the list, and a control dangling
          below body copy is the thing the design brief calls a footnote
          nobody presses. */}
      <Row
        label={
          <Button size="sm" onClick={save} disabled={saveProfile.isPending}>
            {saveProfile.isPending ? 'Saving…' : 'Save profile'}
          </Button>
        }
      />
    </RowGroup>
  );
}

// ── Notifications ──────────────────────────────────────────────────────────

function NotificationFields({ rows }: { rows: NotificationPreference[] }) {
  const savePreference = useSaveNotificationPreference();
  return (
    <RowGroup
      title="Notifications"
      note={
        <>
          {/* Two sentences, two elements. The first is the state — how many are
              off right now — and the second is the thing a switch cannot say.
              Kept apart so each reads as what it is. */}
          <span className="settings-slack-summary">{slackSummary(rows)}</span>{' '}
          <span>
            These control what posts to <strong>#tap-notif</strong>. Everything still appears in
            your Inbox either way — nothing is hidden from you, and muting a specific task still
            wins over anything set here.
          </span>
        </>
      }
    >
      {PREFERENCE_EVENTS.map((event) => {
        const state = preferenceFor(rows, event);
        return (
          <Row
            key={event}
            label={preferenceLabel(event)}
            control={
              <label className="settings-switch">
                <input
                  type="checkbox"
                  checked={state.slack_enabled}
                  aria-label={`Post ${preferenceLabel(event)} to Slack`}
                  onChange={(changed) =>
                    savePreference.mutate({
                      eventType: event,
                      slackEnabled: changed.target.checked,
                      inAppAlertEnabled: state.in_app_alert_enabled,
                    })
                  }
                />
              </label>
            }
          />
        );
      })}
    </RowGroup>
  );
}

// ── Workspace ──────────────────────────────────────────────────────────────

/**
 * The two number fields keep a draft and commit on blur.
 *
 * Writing on every keystroke would be wrong twice over: typing "14" saves 1
 * and then 14, and the field is bound to the server copy, so between the write
 * and the refetch it snaps back to the old number under the cursor. Blur is
 * also where an emptied field is caught — Number('') is 0, which is a legal
 * value for one of these two and would have been saved silently.
 */
function WorkspaceFields({ settings, isAdmin }: { settings: WorkspaceSettings; isAdmin: boolean }) {
  const saveWorkspace = useSaveWorkspaceSettings();
  const [taskDays, setTaskDays] = useState(String(settings.default_task_due_days));
  const [sampleDays, setSampleDays] = useState(String(settings.default_sample_due_days));
  const [problem, setProblem] = useState('');

  function commit(patch: Partial<WorkspaceSettings>) {
    const found = workspaceProblem(patch);
    if (found !== null) {
      setProblem(found);
      return;
    }
    setProblem('');
    saveWorkspace.mutate(patch, { onError: (cause: Error) => setProblem(cause.message) });
  }

  function commitNumber(
    key: 'default_task_due_days' | 'default_sample_due_days',
    raw: string,
    current: number,
  ) {
    const parsed = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(parsed)) {
      setProblem('That field needs a number.');
      return;
    }
    if (parsed === current) {
      setProblem('');
      return;
    }
    commit({ [key]: parsed });
  }

  return (
    <RowGroup
      title="Workspace"
      note={
        isAdmin
          ? 'Defaults applied to new work across the whole team.'
          : 'Defaults applied to new work. Only a TAP admin can change these.'
      }
    >
      {problem === '' ? null : (
        <div className="ui-row ui-row--plain settings-row-error">
          <InlineError>{problem}</InlineError>
        </div>
      )}
      <Row
        icon="tasks"
        label="Default task due days"
        control={
          <Input
            label="Default task due days"
            labelHidden
            type="number"
            value={taskDays}
            disabled={!isAdmin}
            onChange={(event) => setTaskDays(event.target.value)}
            onBlur={() =>
              commitNumber('default_task_due_days', taskDays, settings.default_task_due_days)
            }
          />
        }
      />
      <Row
        icon="orders"
        label="Default sample due days"
        control={
          <Input
            label="Default sample due days"
            labelHidden
            type="number"
            value={sampleDays}
            disabled={!isAdmin}
            onChange={(event) => setSampleDays(event.target.value)}
            onBlur={() =>
              commitNumber('default_sample_due_days', sampleDays, settings.default_sample_due_days)
            }
          />
        }
      />
      <Row
        label="Require a due date on every task"
        control={
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={settings.require_due_date_on_tasks}
              disabled={!isAdmin}
              aria-label="Require a due date on every task"
              onChange={(event) => commit({ require_due_date_on_tasks: event.target.checked })}
            />
          </label>
        }
      />
      <Row
        label="Members may create projects"
        control={
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={settings.allow_member_project_creation}
              disabled={!isAdmin}
              aria-label="Members may create projects"
              onChange={(event) => commit({ allow_member_project_creation: event.target.checked })}
            />
          </label>
        }
      />
    </RowGroup>
  );
}
